/**
 * spaces-analytics-events.ts
 *
 * Pure functions that decide WHAT analytics event to emit (or null) for each
 * spaces action. Extracted from use-spaces.ts so the emission-gate logic can
 * be unit-tested directly without mounting a React hook.
 *
 * Each function receives the action outcome + the call-site metadata and returns
 * either a partial event payload (caller spreads in the envelope) or null (no event).
 *
 * Gate rules (mirroring the schema docstrings in analytics.ts):
 *   - persisted=false → null (changes that did not durably persist MUST NOT emit)
 *   - op failed (ok=false) → never called (callers early-return on !result.ok)
 *   - method=null (TV/programmatic) → null for switchSpace
 *   - fromIndex/toIndex undefined → null for reorderSpaces (cancelled drag)
 */
import type { AnalyticsEvent } from 'briefpane/contracts';

/** Envelope fields spread by the caller — not computed here. */
type Envelope = Omit<AnalyticsEvent, 'event'>;

// --------------------------------------------------------------------------
// Return types — partial event bodies (caller spreads envelope on top)
// --------------------------------------------------------------------------

type SpaceCreatedPayload  = { event: 'space_created';  space_count: number };
type SpaceSwitchedPayload = { event: 'space_switched'; method: 'tab' | 'pill' | 'shortcut'; space_count: number };
type SpaceRenamedPayload  = { event: 'space_renamed';  method: 'inline' | 'popover'; space_count: number };
type SpaceDeletedPayload  = { event: 'space_deleted';  space_count: number; was_active: boolean };
type SpaceDuplicatedPayload = { event: 'space_duplicated'; space_count: number };
type SpaceReorderedPayload  = { event: 'space_reordered'; from_index: number; to_index: number; space_count: number };

// Make Envelope optional in return so callers can treat the payload as the full event.
// Callers spread `...buildEnvelope()` before passing to safeEmit.
export type SpacesAnalyticsPayload =
  | SpaceCreatedPayload
  | SpaceSwitchedPayload
  | SpaceRenamedPayload
  | SpaceDeletedPayload
  | SpaceDuplicatedPayload
  | SpaceReorderedPayload;

// --------------------------------------------------------------------------
// Gate functions — each returns the event payload or null
// --------------------------------------------------------------------------

/**
 * Decide whether to emit `space_created`.
 * null when persisted=false (quota failure — change did not durably persist).
 */
export function spaceCreatedEvent(
  persisted: boolean,
  spaceCount: number,
): SpaceCreatedPayload | null {
  if (!persisted) return null;
  return { event: 'space_created', space_count: spaceCount };
}

/**
 * Decide whether to emit `space_switched`.
 * null when:
 *   - persisted=false (quota failure)
 *   - method=null (TV/programmatic resolution — §1.S.2: "NOT fired for TV mode ?space= URL resolution")
 */
export function spaceSwitchedEvent(
  persisted: boolean,
  method: 'tab' | 'pill' | 'shortcut' | null,
  spaceCount: number,
): SpaceSwitchedPayload | null {
  if (!persisted) return null;
  if (method === null) return null;
  return { event: 'space_switched', method, space_count: spaceCount };
}

/**
 * Decide whether to emit `space_renamed`.
 * null when persisted=false.
 */
export function spaceRenamedEvent(
  persisted: boolean,
  method: 'inline' | 'popover',
  spaceCount: number,
): SpaceRenamedPayload | null {
  if (!persisted) return null;
  return { event: 'space_renamed', method, space_count: spaceCount };
}

/**
 * Decide whether to emit `space_deleted`.
 * null when persisted=false.
 */
export function spaceDeletedEvent(
  persisted: boolean,
  spaceCount: number,
  wasActive: boolean,
): SpaceDeletedPayload | null {
  if (!persisted) return null;
  return { event: 'space_deleted', space_count: spaceCount, was_active: wasActive };
}

/**
 * Decide whether to emit `space_duplicated`.
 * null when persisted=false.
 */
export function spaceDuplicatedEvent(
  persisted: boolean,
  spaceCount: number,
): SpaceDuplicatedPayload | null {
  if (!persisted) return null;
  return { event: 'space_duplicated', space_count: spaceCount };
}

/**
 * Decide whether to emit `space_reordered`.
 * null when:
 *   - persisted=false (quota failure)
 *   - fromIndex or toIndex is undefined (cancelled drag — no drag metadata provided)
 *     §1.S.4: "NOT fired on cancelled or reverted drags (drag.active === false on pointerUp)"
 */
export function spaceReorderedEvent(
  persisted: boolean,
  fromIndex: number | undefined,
  toIndex: number | undefined,
  spaceCount: number,
): SpaceReorderedPayload | null {
  if (!persisted) return null;
  if (fromIndex === undefined || toIndex === undefined) return null;
  return { event: 'space_reordered', from_index: fromIndex, to_index: toIndex, space_count: spaceCount };
}

// --------------------------------------------------------------------------
// Convenience: apply an envelope to a payload for direct emit
// --------------------------------------------------------------------------

/**
 * Merge a payload returned by one of the gate functions with the standard
 * client envelope, producing a complete AnalyticsEvent ready for safeEmit.
 * Call only when payload !== null.
 */
export function withEnvelope(
  payload: SpacesAnalyticsPayload,
  envelope: Envelope,
): AnalyticsEvent {
  return { ...envelope, ...payload } as AnalyticsEvent;
}
